<template>
  <div class="flex flex-col h-full">
    <!-- Products associated to this conversation -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="associatedProducts.length === 0" class="flex flex-col items-center justify-center py-12 text-n-slate-10">
        <span class="text-4xl mb-3">📦</span>
        <p class="text-sm m-0">Nenhum produto associado</p>
        <p class="text-xs m-0 mt-1">Adicione produtos a este negócio</p>
      </div>

      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="(item, index) in associatedProducts"
          :key="item.productId"
          class="px-4 py-3 hover:bg-n-alpha-1 transition-colors"
        >
          <div class="flex items-center justify-between mb-2">
            <div class="min-w-0">
              <span class="text-sm font-semibold text-n-slate-12">{{ getProductName(item.productId) }}</span>
              <span class="text-xs text-n-slate-10 ml-2">{{ formatPrice(getProductPrice(item.productId)) }}/un</span>
            </div>
            <button
              class="p-1 rounded hover:bg-red-500/20 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-red-400"
              @click="removeProduct(index)"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
          <div class="flex items-center gap-3">
            <div class="flex items-center gap-1">
              <label class="text-xs text-n-slate-10">Qtd:</label>
              <input
                :value="item.quantity"
                type="number"
                min="1"
                class="w-16 px-2 py-1 text-xs border border-n-weak rounded bg-n-surface-1 text-center"
                @change="updateQuantity(index, $event)"
              />
            </div>
            <div class="flex items-center gap-1">
              <label class="text-xs text-n-slate-10">Desc %:</label>
              <input
                :value="item.discount"
                type="number"
                min="0"
                max="100"
                class="w-16 px-2 py-1 text-xs border border-n-weak rounded bg-n-surface-1 text-center"
                @change="updateDiscount(index, $event)"
              />
            </div>
            <span class="text-xs font-bold text-emerald-500 ml-auto">
              {{ formatPrice(lineTotal(item)) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Total -->
      <div v-if="associatedProducts.length > 0" class="px-4 py-3 border-t border-n-weak bg-n-alpha-1">
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-n-slate-12">Total do Negócio</span>
          <span class="text-base font-bold text-emerald-500">{{ formatPrice(totalValue) }}</span>
        </div>
      </div>
    </div>

    <!-- Add product -->
    <div class="p-3 border-t border-n-weak space-y-2">
      <select
        v-model="selectedProductId"
        class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm"
      >
        <option value="">Selecione um produto...</option>
        <option
          v-for="product in availableProducts"
          :key="product.id"
          :value="product.id"
        >
          {{ product.name }} — {{ formatPrice(product.price) }}
        </option>
      </select>
      <button
        class="button success w-full"
        :disabled="!selectedProductId"
        @click="addProduct"
      >
        + Adicionar Produto
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductList',
  props: {
    conversationProducts: {
      type: Array,
      default: () => [],
    },
    catalogProducts: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update-products'],
  data() {
    return {
      selectedProductId: '',
    };
  },
  computed: {
    associatedProducts() {
      return this.conversationProducts || [];
    },
    availableProducts() {
      const usedIds = this.associatedProducts.map(p => p.productId);
      return this.catalogProducts.filter(p => !usedIds.includes(p.id));
    },
    totalValue() {
      return this.associatedProducts.reduce((acc, item) => acc + this.lineTotal(item), 0);
    },
  },
  methods: {
    formatPrice(val) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
    },
    getProductName(id) {
      const p = this.catalogProducts.find(p => p.id === id);
      return p ? p.name : 'Produto removido';
    },
    getProductPrice(id) {
      const p = this.catalogProducts.find(p => p.id === id);
      return p ? p.price : 0;
    },
    lineTotal(item) {
      const price = this.getProductPrice(item.productId);
      const qty = item.quantity || 1;
      const disc = item.discount || 0;
      return price * qty * (1 - disc / 100);
    },
    addProduct() {
      if (!this.selectedProductId) return;
      const updated = [
        ...this.associatedProducts,
        { productId: this.selectedProductId, quantity: 1, discount: 0 },
      ];
      this.$emit('update-products', updated);
      this.selectedProductId = '';
    },
    removeProduct(index) {
      const updated = this.associatedProducts.filter((_, i) => i !== index);
      this.$emit('update-products', updated);
    },
    updateQuantity(index, event) {
      const val = parseInt(event.target.value) || 1;
      const updated = [...this.associatedProducts];
      updated[index] = { ...updated[index], quantity: Math.max(1, val) };
      this.$emit('update-products', updated);
    },
    updateDiscount(index, event) {
      const val = parseInt(event.target.value) || 0;
      const updated = [...this.associatedProducts];
      updated[index] = { ...updated[index], discount: Math.min(100, Math.max(0, val)) };
      this.$emit('update-products', updated);
    },
  },
};
</script>
